import { Anchor, Code, Group, Select, Stack, Table, TextInput, Title } from '@mantine/core';
import { useState } from 'react';
import { Link } from 'react-router';

import { useAuditLog } from '../../features/audit';
import { formatDateTime } from '../../shared/format';
import { CursorPager, PageFallback, hasItems, useCursorPager } from '../../shared/ui';

/** Действия — как их пишет бэкенд (`AUDIT_ACTIONS`); подписи — для человека. */
const ACTIONS = [
  { value: 'user.role.granted', label: 'Роль выдана' },
  { value: 'user.role.revoked', label: 'Роль отозвана' },
  // D-19 бэкенда: чтение росписи администратором — единственное чтение, которое пишется в
  // журнал, по записи на каждую отданную страницу (в `details.seats` — сколько строк).
  { value: 'practice.roster.read', label: 'Роспись прочитана' },
];

const ACTION_LABEL = Object.fromEntries(ACTIONS.map((action) => [action.value, action.label]));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AuditPage() {
  const [action, setAction] = useState<string | null>(null);
  const [actor, setActor] = useState('');
  // Идентификатор уходит в запрос, только когда он целый: половина UUID — это `400` от API,
  // а не «пока пусто».
  const actorFilter = UUID.test(actor.trim()) ? actor.trim() : undefined;
  const filters = { action: action ?? undefined, actor: actorFilter };
  const pager = useCursorPager(JSON.stringify(filters));
  const page = useAuditLog(filters, pager.cursor);

  return (
    <Stack gap="md">
      <Title order={2}>Журнал аудита</Title>
      <Group align="flex-end">
        <Select
          label="Действие"
          data={ACTIONS}
          value={action}
          onChange={setAction}
          clearable
          w={220}
        />
        <TextInput
          label="Актор (id аккаунта)"
          placeholder="uuid"
          value={actor}
          onChange={(event) => setActor(event.currentTarget.value)}
          w={340}
        />
      </Group>

      <PageFallback query={page} empty="Записей нет." />
      {hasItems(page) ? (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Когда</Table.Th>
              <Table.Th>Кто</Table.Th>
              <Table.Th>Что</Table.Th>
              <Table.Th>Кому</Table.Th>
              <Table.Th>Детали</Table.Th>
              <Table.Th>Запрос</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {page.data?.items.map((entry) => (
              <Table.Tr key={entry.id}>
                <Table.Td>{formatDateTime(entry.createdAt)}</Table.Td>
                <Table.Td>
                  <Anchor component={Link} to={`/users/${entry.actorUserId}`} size="sm">
                    {entry.actorUserId.slice(0, 8)}…
                  </Anchor>
                </Table.Td>
                <Table.Td>{ACTION_LABEL[entry.action] ?? entry.action}</Table.Td>
                <Table.Td>
                  {entry.targetType === 'user' ? (
                    <Anchor component={Link} to={`/users/${entry.targetId}`} size="sm">
                      {entry.targetId.slice(0, 8)}…
                    </Anchor>
                  ) : entry.targetType === 'practice' ? (
                    <Anchor component={Link} to="/practices" size="sm">
                      практика {entry.targetId.slice(0, 8)}…
                    </Anchor>
                  ) : (
                    `${entry.targetType} ${entry.targetId.slice(0, 8)}…`
                  )}
                </Table.Td>
                <Table.Td>
                  {entry.details ? <Code>{JSON.stringify(entry.details)}</Code> : '—'}
                </Table.Td>
                <Table.Td>
                  <Code>{entry.correlationId}</Code>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}
      <CursorPager query={page} pager={pager} />
    </Stack>
  );
}
